import React, { useEffect, useState, useMemo } from "react";
import "../css/PythagoreanTriplesLab.scss";
import PythagSquare from "./PythagSquare";

const PRESET_CORNERS = [1, 2, 3, 4, 5, 8, 9];
const DEFAULT_MAX_C = 5000;
const MAX_RESULTS = 50;

function gcd(a, b) {
    while (b !== 0) {
        const t = b;
        b = a % b;
        a = t;
    }
    return Math.abs(a);
}

function gcd3(a, b, c) {
    return gcd(gcd(a, b), c);
}

function generateTriples(k, maxC, maxResults) {
    const triples = [];
    const absK = Math.abs(k);
    if (absK === 0) return triples;

    for (let a = 3; a <= maxC; a++) {
        const a2 = a * a;
        const k2 = absK * absK;
        const numerator = a2 - k2;
        const denominator = 2 * absK;

        if (numerator <= 0) continue;
        if (numerator % denominator !== 0) continue;

        const b = numerator / denominator;
        const c = b + absK;

        if (!Number.isInteger(b) || b <= 0 || c > maxC) continue;
        if (a >= b) continue; // enforce ordering a < b < c

        const g = gcd3(a, b, c);
        const primitive = g === 1;

        triples.push({ a, b, c, primitive, gcd: g });

        if (triples.length >= maxResults) break;
    }

    return triples;
}


function TriplesList({ triples, onSelectTriple }) {
    if (triples.length === 0) {
        return (
            <section className="pt-triples">
                <div className="pt-card">
                    <div className="pt-card-title">Triples</div>
                    <p>No triples found for this corner within the current range.</p>
                </div>
            </section>
        );
    }

    return (
        <section className="pt-triples">
            <div className="pt-card">
                <table className="pt-table">
                    <thead>
                        <tr>
                            <th>Triple</th>
                            <th>Equation</th>
                            <th>GCD / Type</th>
                        </tr>
                    </thead>

                    <tbody>
                        {triples.map((t, idx) => (
                            <tr
                                key={`${t.a}-${t.b}-${t.c}-${idx}`}
                                className="pt-result-row"
                                tabIndex="0"
                                onClick={() => onSelectTriple(t)}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        onSelectTriple(t);
                                    }
                                }}
                            >
                                <td>({t.a}, {t.b}, {t.c})</td>
                                <td>{t.a}² + {t.b}² = {t.c}²</td>
                                <td className="pt-gcd-cell">
                                    <span className="pt-gcd-content">
                                        {t.primitive ? (
                                            <span className="pt-primitive-badge">
                                                Primitive
                                            </span>
                                        ) : (
                                            <span>{t.gcd}</span>
                                        )}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>

                </table>
            </div>
        </section>
    );
}

function TripleModal({ triple, onClose }) {
    const [highlightedArea, setHighlightedArea] = useState("c");

    useEffect(() => {
        setHighlightedArea("c");
    }, [triple]);

    if (!triple) return null;

    const { a, b, c } = triple;
    const canShowSquareGrid = c <= 50;

    return (
        <div className="pt-modal-backdrop pt-triple-modal-backdrop" onClick={onClose}>
            <div
                className="pt-modal pt-triple-modal"
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                <header className="pt-modal-header">
                    <div className="pt-modal-title">
                        ({a}, {b}, {c})
                    </div>

                    <button
                        className="pt-modal-close-btn"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </header>

                <div className="pt-modal-body">
                    {canShowSquareGrid ? (
                        <>
                            <div
                                className="pt-square-summary-controls"
                                role="group"
                                aria-label="Highlight a square region"
                            >
                                {[
                                    { area: "c", value: c },
                                    { operator: "=" },
                                    { area: "a", value: a },
                                    { operator: "+" },
                                    { area: "b", value: b },
                                ].map(({ area, value, operator }, index) =>
                                    operator ? (
                                        <span
                                            className="pt-square-summary-operator"
                                            aria-hidden="true"
                                            key={`${operator}-${index}`}
                                        >
                                            {operator}
                                        </span>
                                    ) : (
                                    <button
                                        key={area}
                                        type="button"
                                        className={highlightedArea === area ? "active" : ""}
                                        aria-pressed={highlightedArea === area}
                                        onClick={() => setHighlightedArea(area)}
                                    >
                                        <i>{area}</i>² = {value * value}
                                    </button>
                                    )
                                )}
                            </div>

                            <div className="pt-modal-grid-wrapper">
                            <PythagSquare
                                triple={[a, b, c]}
                                highlightedArea={highlightedArea}
                            />
                            </div>
                        </>
                    ) : (
                        <div
                            className="pt-large-triple-equation"
                            aria-label={`${c} squared equals ${a} squared plus ${b} squared`}
                        >
                            <div className="pt-large-triple-equation-heading">
                                Pythagorean relationship
                            </div>
                            <div className="pt-triangle-area-animation" aria-hidden="true">
                                <svg viewBox="0 0 280 170" focusable="false">
                                    <path d="M58 20 V145 H248 Z" />
                                    <path className="pt-triangle-right-angle" d="M58 131 H72 V145" />
                                </svg>
                                <span className="pt-triangle-side-label pt-triangle-side-a">
                                    <i>a</i>² = {(a * a).toLocaleString()}
                                </span>
                                <span className="pt-triangle-side-label pt-triangle-side-b">
                                    <i>b</i>² = {(b * b).toLocaleString()}
                                </span>
                                <span className="pt-triangle-ghost pt-triangle-ghost-a">
                                    {(a * a).toLocaleString()}
                                </span>
                                <span className="pt-triangle-ghost pt-triangle-ghost-b">
                                    {(b * b).toLocaleString()}
                                </span>
                                <span className="pt-triangle-merge-result">
                                    <i>c</i>² = {(c * c).toLocaleString()}
                                </span>
                            </div>
                            <div className="pt-large-triple-equation-row pt-large-triple-equation-symbols" aria-hidden="true">
                                <span><i>c</i>²</span><b>=</b><span><i>a</i>²</span><b>+</b><span><i>b</i>²</span>
                            </div>
                            <div className="pt-large-triple-equation-row">
                                <span>{c}²</span><b>=</b><span>{a}²</span><b>+</b><span>{b}²</span>
                            </div>
                            <div className="pt-large-triple-equation-row pt-large-triple-equation-values">
                                <span>{(c * c).toLocaleString()}</span><b>=</b><span>{(a * a).toLocaleString()}</span><b>+</b><span>{(b * b).toLocaleString()}</span>
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}


function InfoModal({ open, onClose }) {
    if (!open) return null;

    return (
        <div className="pt-modal-backdrop" onClick={onClose}>
            <div
                className="pt-modal pt-info-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="pt-modal-header">
                    <h2>How Pythagorean Triples Work</h2>
                    <button className="pt-modal-close" onClick={onClose}>
                        ✕
                    </button>
                </header>
                <div className="pt-modal-body">
                    <p>
                        The classic Pythagorean triple is (3, 4, 5). If you draw squares on
                        each side of the right triangle, the 3×3 and 4×4 squares can be
                        rearranged to exactly fill the 5×5 square.
                    </p>
                    <p>
                        For odd values of a, you can generate a triple using
                        <br />
                        <code>b = (a² − 1) / 2</code> and <code>c = (a² + 1) / 2</code>.
                    </p>
                    <p>
                        On this page, we instead fix the difference c − b (the{" "}
                        <strong>corner value</strong>) and explore all triples that share
                        that difference. The lab lets you:
                    </p>
                    <ul>
                        <li>Choose a corner value c − b</li>
                        <li>See all triples that match it (within a range)</li>
                        <li>Check which of them are primitive (gcd(a, b, c) = 1)</li>
                        <li>Click a triple to visualize it as squares</li>
                    </ul>
                </div>
                <footer className="pt-modal-footer">
                    <button className="pt-btn" onClick={onClose}>
                        Back to Lab
                    </button>
                </footer>
            </div>
        </div>
    );
}



// Only use small primes up to 7 now
const PRIME_OPTIONS = [2, 3, 5, 7];

function PrimeCornerBuilder({ onApply }) {
    const [exponents, setExponents] = useState(
        () => PRIME_OPTIONS.reduce((acc, p) => ({ ...acc, [p]: 0 }), {})
    );

    const updateExponent = (p, delta) => {
        setExponents((prev) => {
            const next = { ...prev };
            next[p] = Math.max(0, (next[p] || 0) + delta);
            return next;
        });
    };

    const { product, factorStr } = useMemo(() => {
        let prod = 1;
        const parts = [];
        PRIME_OPTIONS.forEach((p) => {
            const e = exponents[p];
            if (e > 0) {
                prod *= p ** e;
                parts.push(e === 1 ? `${p}` : `${p}^${e}`);
            }
        });
        return {
            product: prod,
            factorStr: parts.length ? parts.join(" · ") : "1",
        };
    }, [exponents]);

    const handleApply = () => {
        if (product !== 1) onApply(product);
    };

    return (
        <div className="pt-prime-builder">
            <div className="pt-prime-builder-title">Build corner from small primes</div>

            <div className="pt-prime-row">
                {PRIME_OPTIONS.map((p) => (
                    <div key={p} className="pt-prime-chip">
                        <span className="pt-prime-label">{p}</span>
                        <button
                            type="button"
                            className="pt-prime-btn"
                            onClick={() => updateExponent(p, -1)}
                        >
                            −
                        </button>
                        <span className="pt-prime-exp">exp {exponents[p]}</span>
                        <button
                            type="button"
                            className="pt-prime-btn"
                            onClick={() => updateExponent(p, +1)}
                        >
                            +
                        </button>
                    </div>
                ))}

                {/* circular apply button with checkmark */}
                <button
                    type="button"
                    className="pt-prime-apply-icon"
                    onClick={handleApply}
                    disabled={product === 1}
                    aria-label="Use prime product as corner"
                >
                    ✓
                </button>
            </div>

            <div className="pt-prime-summary">
                <div>
                    Factorization: <strong>{factorStr}</strong>
                </div>
                <div>
                    Product: <strong>{product}</strong>
                </div>
            </div>
        </div>
    );
}

function CornerInfoTooltip({ open, onClose }) {
    if (!open) return null;

    return (
        <div className="pt-corner-tooltip-backdrop" onClick={onClose}>
            <div
                className="pt-corner-tooltip"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="pt-corner-tooltip-title">What is the corner?</div>

                <div className="pt-corner-tooltip-graphic">
                    {/* Tiny visual diagram modeled on c = 5, b = 4, a = 3 */}
                    <svg width="150" height="150">
                        {/* c² background */}
                        <rect
                            x="10"
                            y="10"
                            width="100"
                            height="100"
                            fill="#f6f6f6"
                            stroke="#555"
                        />

                        {/* vertical strip on the left: width = c - b */}
                        <rect
                            x="10"
                            y="30"
                            width="20"
                            height="80"
                            fill="#999999"
                            stroke="#f6f6f6"
                            strokeWidth="1"
                        />

                        {/* horizontal strip on top: height = c - b */}
                        <rect
                            x="30"
                            y="10"
                            width="80"
                            height="20"
                            fill="#999999"
                            stroke="#f6f6f6"
                            strokeWidth="1"
                        />

                        {/* corner square (c−b)²: overlaps of the two strips */}
                        <rect
                            x="10"
                            y="10"
                            width="20"
                            height="20"
                            fill="#000099"
                            stroke="#f6f6f6"
                            strokeWidth="1"
                        />

                        {/* b² anchored at lower-right */}
                        <rect
                            x="30"
                            y="30"
                            width="80"
                            height="80"
                            fill="#666"
                            stroke="#f6f6f6"
                            strokeWidth="1"
                        />

                        {/* Labels */}
                        <text x="58" y="72" fontSize="9" fill="#f6f6f6">
                            b²
                        </text>
                        <text x="52" y="126" fontSize="9" fill="#333">
                            c²
                        </text>
                    </svg>
                </div>

                <p className="pt-corner-tooltip-text">
                    We draw the <strong>c²</strong> square and place the{" "}
                    <strong>b²</strong> square snug in the lower right corner. This
                    leaves a vertical strip on the left and a horizontal strip across
                    the top. Each strip has thickness <strong>c − b</strong>.
                </p>
                <p className="pt-corner-tooltip-text">
                    Where those two strips overlap in the upper left, you get a small
                    square of size <strong>(c − b) × (c − b)</strong>. That little
                    square is what we call the <strong>corner</strong>.
                </p>

                <button className="pt-btn" onClick={onClose}>
                    Close
                </button>
            </div>
        </div>
    );
}


export default function PythagoreanTriplesLab() {
    const [corner, setCorner] = useState(1);
    const [customInput, setCustomInput] = useState("225");
    const [selectedTriple, setSelectedTriple] = useState(null);
    const [showInfo, setShowInfo] = useState(false);
    const [showCornerInfo, setShowCornerInfo] = useState(false);

    const [cornerCollapsed, setCornerCollapsed] = useState(false);
    const [primeBuilderOpen, setPrimeBuilderOpen] = useState(false);

    const triples = useMemo(
        () => generateTriples(corner, DEFAULT_MAX_C, MAX_RESULTS),
        [corner]
    );

    // NEW: summary info for the collapsed pill
    const primitiveCount = triples.filter((t) => t.primitive).length;
    const primitivesText = primitiveCount > 0 ? "Yes" : "None";

    const handlePresetClick = (k) => {
        setCorner(k);
        setCustomInput(String(k));
        setCornerCollapsed(true);
    };

    const handleCustomSubmit = (e) => {
        e.preventDefault();
        const val = parseInt(customInput, 10);
        if (!Number.isNaN(val) && val !== 0) {
            setCorner(val);
            setPrimeBuilderOpen(false);
            setCornerCollapsed(true);
        }
    };



    return (
        <div className="pt-page">

            <div className="math-toy-page-header">
                <h1 className="math-toy-page-title pt-page-title">Pythagorean Triples Lab</h1>
            </div>

            <main className="pt-main">
                {/* Corner selector */}


                <section className="pt-corner-container">
                    {cornerCollapsed ? (
                        <div className="pt-corner-collapsed">
                            <button
                                type="button"
                                className="pt-corner-back"
                                onClick={() => setCornerCollapsed(false)}
                                aria-label="Choose another corner"
                            >
                                <svg
                                    className="pt-corner-back-icon"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                >
                                    <path d="M19 12H5" />
                                    <path d="M11 18l-6-6 6-6" />
                                </svg>
                            </button>

                            <div className="pt-corner-summary">
                                <span className="pt-summary-label">Corner (c − b)</span>
                                <span className="pt-summary-value">{corner}</span>

                                <span className="pt-summary-sep">·</span>
                                <span className="pt-summary-label">Primitives</span>
                                <span className="pt-summary-value">{primitivesText}</span>
                            </div>
                        </div>
                    ) : (
                        // EXPANDED TOOLS
                        <div className="pt-corner-selector">
                            <div className="pt-section-title">
                                Corner (c − b)
                                <button
                                    type="button"
                                    className="pt-info-icon"
                                    onClick={() => setShowCornerInfo(true)}
                                    aria-label="What is corner?"
                                >
                                    ⓘ
                                </button>
                            </div>


                            <div className="pt-chip-row">
                                {PRESET_CORNERS.map((k) => (
                                    <button
                                        key={k}
                                        className={
                                            corner === k ? "pt-chip pt-chip-active" : "pt-chip"
                                        }
                                        type="button"
                                        onClick={() => handlePresetClick(k)}
                                    >
                                        {k}
                                    </button>
                                ))}

                            </div>

                            <button
                                type="button"
                                className="pt-advanced-toggle"
                                aria-expanded={primeBuilderOpen}
                                onClick={() => setPrimeBuilderOpen((open) => !open)}
                            >
                                <span>Advanced</span>
                                <span aria-hidden="true">{primeBuilderOpen ? "▴" : "▾"}</span>
                            </button>

                            {primeBuilderOpen && (
                                <div className="pt-advanced-tools">
                                    <form
                                        className="pt-inline-custom"
                                        onSubmit={handleCustomSubmit}
                                    >
                                        <span className="pt-inline-label">Custom corner</span>
                                        <input
                                            className="pt-input"
                                            type="number"
                                            value={customInput}
                                            onChange={(e) => setCustomInput(e.target.value)}
                                        />
                                        <button className="pt-btn" type="submit">
                                            Go
                                        </button>
                                    </form>

                                    <PrimeCornerBuilder
                                        onApply={(value) => {
                                            setCorner(value);
                                            setCustomInput(String(value));
                                            setPrimeBuilderOpen(false);
                                            setCornerCollapsed(true);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </section>


                {/* Triples list */}
                <TriplesList
                    triples={triples}
                    onSelectTriple={(t) => setSelectedTriple(t)}
                />

                {/* Info link */}
                <section className="pt-info-link-section">
                    <button
                        className="pt-info-link"
                        type="button"
                        onClick={() => setShowInfo(true)}
                    >
                        ℹ︎ How this works
                    </button>
                </section>
            </main>

            {/* Modals */}
            <TripleModal
                triple={selectedTriple}
                onClose={() => setSelectedTriple(null)}
            />
            <InfoModal open={showInfo} onClose={() => setShowInfo(false)} />

            <CornerInfoTooltip
                open={showCornerInfo}
                onClose={() => setShowCornerInfo(false)}
            />

        </div>
    );
}
